import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';

// Critical above-the-fold components — loaded eagerly
import Header from './components/Header';
import Footer from './components/Footer';
import StickyBookNow from './components/StickyBookNow';
import { ACTIVE_SUBURBS } from './data/suburbs';
import { SERVICE_SUBURB_ROUTES } from './data/serviceSuburbContent';

// Route-level code splitting — these chunks are fetched lazily only when a user
// navigates to that route. Cuts initial JS by ~60-70%, dramatically improving
// Total Blocking Time and Largest Contentful Paint on mobile.
// HomePage is lazy too so its <SEOHead> mounts *after* HelmetProvider settles
// (helmet-async v2 + StrictMode drops synchronous mounts' meta tags otherwise).
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const CookiesPolicyPage = lazy(() => import('./pages/CookiesPolicyPage'));
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const FoundingClientPage = lazy(() => import('./pages/FoundingClientPage'));
const AreasWeServicePage = lazy(() => import('./pages/AreasWeServicePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const BeautyTrendsPage = lazy(() => import('./pages/BeautyTrendsPage'));
const ShippingReturnsPage = lazy(() => import('./pages/ShippingReturnsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const OurStoryPage = lazy(() => import('./pages/OurStoryPage'));
const AdminSignupsPage = lazy(() => import('./pages/AdminSignupsPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AdminBlogPage = lazy(() => import('./pages/AdminBlogPage'));
const AdminPagesPage = lazy(() => import('./pages/AdminPagesPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const SuburbPage = lazy(() => import('./pages/SuburbPage'));
const ConcernPage = lazy(() => import('./pages/ConcernPage'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const SkinQuizPage = lazy(() => import('./pages/SkinQuizPage'));
const MuradStatementsPage = lazy(() => import('./pages/MuradStatementsPage'));
const GiftCardPage = lazy(() => import('./pages/GiftCardPage'));
const AftercarePage = lazy(() => import('./pages/AftercarePage'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage'));
const BookingFlow = lazy(() => import('./pages/BookingFlow'));
const AIRecommendation = lazy(() => import('./components/AIRecommendation'));
const BookingConfirmation = lazy(() => import('./components/BookingConfirmation'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));

// Non-critical UI loaded after first paint
const CartDrawer = lazy(() => import('./components/CartDrawer'));
const WelcomePopup = lazy(() => import('./components/WelcomePopup'));

import './App.css';

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center" data-testid="route-loading">
    <div className="w-8 h-8 rounded-full border-2 border-beauty-green/30 border-t-beauty-green animate-spin"></div>
  </div>
);

/**
 * Sets `<meta name="robots" content="noindex,nofollow">` directly on the
 * document for utility routes (admin dashboards, my-account, payment success,
 * booking confirmation). Uses a direct DOM effect rather than react-helmet-async
 * so the tag is deterministically present on every navigation — helmet-async
 * batching was dropping this on client-side transitions during testing.
 */
const RouteNoIndex = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isNoIndex =
      pathname.startsWith('/admin') ||
      pathname.startsWith('/my-account') ||
      pathname.startsWith('/payment-success') ||
      pathname.startsWith('/booking-confirmation');

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    let gbot = document.querySelector('meta[name="googlebot"]');
    if (!gbot) {
      gbot = document.createElement('meta');
      gbot.setAttribute('name', 'googlebot');
      document.head.appendChild(gbot);
    }
    const content = isNoIndex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    robots.setAttribute('content', content);
    gbot.setAttribute('content', content);
  }, [pathname]);

  return null;
};

// Only the 6 closest suburbs keep a dedicated page (doorway-page cleanup, Oct 2026);
// the rest redirect to Areas We Service so old links and indexed URLs still resolve.
const SuburbRoute = () => {
  const { slug } = useParams();
  return ACTIVE_SUBURBS.includes(slug) ? <SuburbPage /> : <Navigate to="/areas-we-service" replace />;
};

const ComboRedirect = () => {
  const { comboSlug } = useParams();
  const combo = SERVICE_SUBURB_ROUTES[comboSlug];
  return <Navigate to={combo ? `/services/${combo.serviceKey}` : '/'} replace />;
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-beauty-white">
        <RouteNoIndex />
        <Header />
        <main className="relative">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/facials" element={<ServicesPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/skin-quiz" element={<SkinQuizPage />} />
              <Route path="/murad-clinical-statements" element={<MuradStatementsPage />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/founding-client" element={<FoundingClientPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/policies" element={<PoliciesPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
              <Route path="/areas-we-service" element={<AreasWeServicePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/beauty-trends" element={<BeautyTrendsPage />} />
              <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/our-story" element={<OurStoryPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/shop/:handle" element={<ProductPage />} />
              <Route path="/facial-near/:slug" element={<SuburbRoute />} />
              <Route path="/treatments/:slug" element={<ConcernPage />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/admin/blog" element={<AdminBlogPage />} />
              <Route path="/admin/pages" element={<AdminPagesPage />} />
              <Route path="/admin/signups" element={<AdminSignupsPage />} />
              <Route path="/gift-cards" element={<GiftCardPage />} />
              <Route path="/aftercare" element={<AftercarePage />} />
              <Route path="/my-account" element={<MyAccountPage />} />
              <Route path="/booking" element={<BookingFlow />} />
              <Route path="/ai-recommendation" element={<AIRecommendation />} />
              <Route path="/confirmation/:bookingId" element={<BookingConfirmation />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              {/* Retired service×suburb combo pages → 301-style redirect to the matching treatment page. */}
              <Route path="/:comboSlug" element={<ComboRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <StickyBookNow />
        <Suspense fallback={null}>
          <CartDrawer />
          <WelcomePopup />
        </Suspense>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              color: '#111111',
              border: '1px solid #E2E2E2',
            },
            className: 'font-sans',
            duration: 4000,
          }}
        />
      </div>
    </Router>
  );
}

export default App;
